import React from 'react';
import {
    Callout,
    CommandBarButton,
    DirectionalHint,
    FontWeights,
    getTheme,
    mergeStyleSets,
    Persona,
    PersonaSize,
    Text,
} from '@fluentui/react';


interface UserPersonaProps {
    email: string;
    displayName?: string;
    onLogout: () => void;
}

function getInitials(name: string): string {
    const parts = name.trim().split(/[\s._-]+/);
    if (parts.length >= 2) {
        return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    // Single word — use first two chars
    return name.slice(0, 2).toUpperCase();
}

const theme = getTheme();

const styles = mergeStyleSets({
    wrapper: {
        position: 'relative' as const,
        display: 'inline-flex',
        flexShrink: 0,
    },
    personaButton: {
        display: 'inline-flex',
        alignItems: 'center',
        cursor: 'pointer',
        padding: '4px',
        borderRadius: '50%',
        border: 'none',
        background: 'transparent',
        appearance: 'none' as const,
        flexShrink: 0,
        selectors: {
            ':hover': {
                background: theme.palette.neutralLighter,
            },
        },
    },
    callout: {
        minWidth: 220,
        padding: '12px 0 8px',
    },
    calloutEmail: {
        padding: '0 16px 12px',
        borderBottom: `1px solid ${theme.palette.neutralLight}`,
        marginBottom: 4,
    },
    emailText: {
        display: 'block',
        fontWeight: FontWeights.semibold,
        color: theme.palette.neutralDark,
        fontSize: 13,
        wordBreak: 'break-all',
    },
    labelText: {
        display: 'block',
        color: theme.palette.neutralSecondary,
        fontSize: 11,
        marginBottom: 2,
    },
    logoutButton: {
        width: '100%',
        padding: '6px 16px',
        color: theme.palette.redDark,
        selectors: {
            ':hover': { background: theme.palette.neutralLighter },
        },
    },
});

export const UserPersona: React.FC<UserPersonaProps> = ({ email, displayName, onLogout }) => {
    const buttonRef = React.useRef<HTMLButtonElement>(null);
    const [isCalloutVisible, setIsCalloutVisible] = React.useState(false);
    const personaText = displayName || email;

    return (
        <div className={styles.wrapper}>
            <button
                ref={buttonRef}
                type="button"
                className={styles.personaButton}
                onClick={() => setIsCalloutVisible(v => !v)}
                aria-label={`Uživatel ${personaText} — klikněte pro možnosti`}
            >
                <Persona
                    text={personaText}
                    size={PersonaSize.size32}
                    imageInitials={getInitials(personaText)}
                    hidePersonaDetails
                    styles={{ root: { cursor: 'pointer' } }}
                />
            </button>

            {isCalloutVisible && (
                <Callout
                    target={buttonRef}
                    onDismiss={() => setIsCalloutVisible(false)}
                    directionalHint={DirectionalHint.bottomRightEdge}
                    gapSpace={6}
                    isBeakVisible={false}
                    setInitialFocus={false}
                    doNotLayer
                    styles={{ root: styles.callout }}
                >
                    <div className={styles.calloutEmail}>
                        <Text className={styles.labelText}>Přihlášen jako</Text>
                        {displayName && (
                            <Text className={styles.emailText}>{displayName}</Text>
                        )}
                        <Text className={styles.emailText} style={displayName ? { fontWeight: 400, fontSize: 12, color: undefined } : undefined}>{email}</Text>
                    </div>
                    <CommandBarButton
                        className={styles.logoutButton}
                        iconProps={{ iconName: 'SignOut' }}
                        text="Odhlásit se"
                        onClick={() => { setIsCalloutVisible(false); onLogout(); }}
                        styles={{
                            root: styles.logoutButton,
                            icon: { color: theme.palette.redDark },
                            label: { color: theme.palette.redDark },
                        }}
                    />
                </Callout>
            )}
        </div>
    );
};
